import { DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { merge } from "rxjs";
import { Aircraft } from "../../../../shared/bootstrap/bootstrap.model";
import { Flight } from "../../../models/flight.model";
import { amount, id, text } from "../../form/controls";
import { convertFluid, litresOf, quantityOf } from "../../form/quantities";

/** The columns a row of fuel keeps its values in: the flight and a refuel before the first flight name them differently. */
export interface FuelFields {
    name: string;
    arrival: string;
    planned: string;
    refuel: string;
    total: string;
    location: string;
}

export const REFUEL_FIELDS: FuelFields = { name: "oilType", arrival: "remainingFuel", planned: "fuelPlanned", refuel: "refuel", total: "total", location: "secondRefuelLocation" };
export const FLIGHT_FUEL_FIELDS: FuelFields = {
    name: "fuelName",
    arrival: "fuelArrival",
    planned: "fuelPlanned",
    refuel: "refuel",
    total: "totalFuel",
    location: "firstRefuelLocation",
};

/** The quantities of a row of fuel, each kept by the logbook as text: `300.0 kg`. */
export const FUEL_QUANTITIES = [
    { key: "arrival", title: "FLIGHT_LIST.fuel.remaining" },
    { key: "planned", title: "FLIGHT_LIST.fuel.planned" },
    { key: "refuel", title: "FLIGHT_LIST.fuel.refuel" },
    { key: "total", title: "FLIGHT_LIST.fuel.total" },
] as const;

export type FuelQuantityKey = (typeof FUEL_QUANTITIES)[number]["key"];

/** The most fuel the tanks of the aircraft take, when the organization checks it. */
export type MaxTank = Aircraft["maxTankCapacity"];

/** What a row of fuel opens with, as the logbook keeps it. */
export interface FuelSource {
    id?: number | null;
    name: string | null;
    arrival: string | null;
    planned: string | null;
    refuel: string | null;
    total: string | null;
    location: string | null;
}

export interface FuelOptions {
    fields: FuelFields;
    units: string[];
    maxTank: MaxTank;
    /** A refuel has an id of its own, which travels with its changes. */
    withId: boolean;
}

export type FuelGroup = FormGroup<Record<string, FormControl<string | number | null>>>;

/**
 * As in the app: fuel needs a fuel name and a refuel quantity, at least one of the refuel, the total and the planned
 * fuel above zero, and neither the refuel (the planned fuel, if none) nor the total over the tanks' capacity.
 * A row that opened empty and stays so is no fuel at all, so it asks for nothing.
 */
function fuelRules(fields: FuelFields, maxTank: MaxTank, openedWithContent: boolean): ValidatorFn {
    const maxLitres = maxTank ? litresOf(maxTank.value, maxTank.unit ?? "lt") : null;
    return (group: AbstractControl): ValidationErrors | null => {
        const row = (group as FuelGroup).getRawValue();
        const number = (key: string) => (typeof row[key] === "number" ? (row[key] as number) : null);
        const unit = (key: string, fallback: string) => (typeof row[key] === "string" && row[key] ? (row[key] as string) : fallback);
        const filled = (key: string) => !!String(row[key] ?? "").trim();
        const hasContent = filled(fields.name) || filled(fields.location) || FUEL_QUANTITIES.some((quantity) => (number(fields[quantity.key]) ?? 0) !== 0);
        if (!hasContent && !openedWithContent) return null;

        const errors: ValidationErrors = {};
        if (!filled(fields.name)) errors["fuelName"] = true;
        if (number(fields.refuel) === null) errors["refuel"] = true;
        const positive = (key: string) => (number(key) ?? 0) > 0;
        if (!positive(fields.refuel) && !positive(fields.total) && !positive(fields.planned)) errors["atLeastOne"] = true;
        if (maxLitres !== null) {
            const loaded =
                number(fields.refuel) !== null
                    ? { value: number(fields.refuel) as number, unit: unit(`${fields.refuel}Unit`, "lt") }
                    : { value: number(fields.planned), unit: unit(`${fields.planned}Unit`, "lt") };
            const loadedLitres = loaded.value === null ? null : litresOf(loaded.value, loaded.unit);
            const totalLitres = number(fields.total) === null ? null : litresOf(number(fields.total) as number, unit(`${fields.total}Unit`, "lt"));
            if ((loadedLitres !== null && loadedLitres > maxLitres) || (totalLitres !== null && totalLitres > maxLitres)) errors["maxTank"] = true;
        }
        return Object.keys(errors).length ? errors : null;
    };
}

/**
 * The calculations of the app: the total is the arrival fuel and the refuel, added in litres and written in the
 * total's unit; the total takes the arrival's unit while it is empty; and the refuel follows the planned fuel until
 * the user types another refuel. They run on what the user changes, never on the values the row opens with.
 */
function watchFuel(group: FuelGroup, fields: FuelFields, destroyRef: DestroyRef): void {
    const control = (key: string) => group.controls[key];
    const arrival = control(fields.arrival);
    const arrivalUnit = control(`${fields.arrival}Unit`);
    const planned = control(fields.planned);
    const plannedUnit = control(`${fields.planned}Unit`);
    const refuel = control(fields.refuel);
    const refuelUnit = control(`${fields.refuel}Unit`);
    const total = control(fields.total);
    const totalUnit = control(`${fields.total}Unit`);

    arrivalUnit.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe((unit) => {
        if (!total.value) totalUnit.setValue(unit);
    });

    let lastSynced: number | null = null;
    merge(planned.valueChanges, plannedUnit.valueChanges)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => {
            // Only what the user does: a programmatic change leaves the controls pristine.
            if (planned.pristine && plannedUnit.pristine) return;
            const value = planned.value as number | null;
            // Clearing the planned fuel never erases the refuel.
            if (!value) return;
            // A refuel of zero is as empty as none: a flight keeps `0 lt` where nothing was refuelled.
            if (!refuel.value || refuel.value === lastSynced) {
                lastSynced = value;
                if (refuel.value !== value) refuel.setValue(value);
                if (plannedUnit.value && refuelUnit.value !== plannedUnit.value) refuelUnit.setValue(plannedUnit.value);
            }
        });

    merge(arrival.valueChanges, arrivalUnit.valueChanges, refuel.valueChanges, refuelUnit.valueChanges, totalUnit.valueChanges)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => {
            const arrivalLitres = convertFluid((arrival.value as number | null) ?? 0, (arrivalUnit.value as string | null) ?? "kg", "lt");
            const refuelLitres = convertFluid((refuel.value as number | null) ?? 0, (refuelUnit.value as string | null) ?? "lt", "lt");
            const sum = Math.round(convertFluid(arrivalLitres + refuelLitres, "lt", (totalUnit.value as string | null) ?? "lt") * 100) / 100;
            if (total.value !== sum) total.setValue(sum);
        });
}

/** A row of fuel: each quantity as a number and a unit, the logbook's `300.0 kg` read the way the app reads it. */
export function buildFuelGroup(source: FuelSource, options: FuelOptions, destroyRef: DestroyRef): FuelGroup {
    const { fields, units, maxTank, withId } = options;
    const controls: Record<string, FormControl<string | number | null>> = {};
    if (withId) controls["id"] = id(source.id ?? null) as FormControl<string | number | null>;
    controls[fields.name] = text(source.name) as FormControl<string | number | null>;
    for (const quantity of FUEL_QUANTITIES) {
        const { value, unit } = quantityOf(source[quantity.key]);
        controls[fields[quantity.key]] = amount(value) as FormControl<string | number | null>;
        controls[`${fields[quantity.key]}Unit`] = text(unit ?? units[0] ?? null) as FormControl<string | number | null>;
    }
    controls[fields.location] = text(source.location) as FormControl<string | number | null>;
    const openedWithContent = source.id != null || [source.name, source.arrival, source.planned, source.refuel, source.total, source.location].some((value) => !!value?.trim());
    const group: FuelGroup = new FormGroup(controls, { validators: fuelRules(fields, maxTank, openedWithContent) });
    watchFuel(group, fields, destroyRef);
    return group;
}

/** The quantities of a row back as the logbook's text, `300.0 kg`; the unit alone is no quantity. */
export function fuelTextsOf(row: Record<string, unknown>, fields: FuelFields): Record<string, unknown> {
    const texts: Record<string, unknown> = { ...row };
    for (const quantity of FUEL_QUANTITIES) {
        const key = fields[quantity.key];
        const value = row[key] as number | null;
        texts[key] = value === null || value === undefined ? null : `${Number.isInteger(value) ? value.toFixed(1) : value} ${row[`${key}Unit`] ?? ""}`.trim();
        delete texts[`${key}Unit`];
    }
    return texts;
}
